/*
================================================================================
                    WPQ TEST AND CERTIFICATE - BUSINESS LOGIC
================================================================================

COMPONENT PURPOSE:
This component handles the final tab of WPQ transaction, managing test results,
certification details, and approval workflow with digital signatures.

KEY BUSINESS PROCESSES:
1. TEST RESULTS MANAGEMENT:
   - Dynamic test table with configurable test types
   - Acceptance criteria definition for each test
   - Test results recording with pass/fail status
   - Comments and report number tracking
   - Minimum 2 test entries required for validation

2. CERTIFICATION TRACKING:
   - Test conducted by personnel identification
   - Mechanical test supervisor assignment
   - RT film evaluation personnel tracking
   - Certification authority validation

3. APPROVAL WORKFLOW:
   - Digital signature integration for prepared by/approved by
   - Role-based approval restrictions (P&T band)
   - Status transition management (Temp → Pending → Approved/Rejected)
   - Approval remarks mandatory for P&T band users
   - Change number increment for approved records

4. DIGITAL SIGNATURE MANAGEMENT:
   - S3-based signature image storage and retrieval
   - Presigned URL generation for secure access
   - User profile integration with signature validation
   - Default image handling for missing signatures

5. FINAL VALIDATION AND SUBMISSION:
   - Comprehensive validation across all three tabs
   - Test array validation for completeness
   - Band-specific validation rules
   - Status synchronization between header and detail records
   - Audit log creation for approval actions

6. DATA PERSISTENCE:
   - Final header record update with approval status
   - Detail records status synchronization
   - Audit log entry for approval workflow
   - Change control through revision numbering

BUSINESS RULES:
- Minimum 2 test entries required for submission
- P&T band users must provide approval remarks
- Approved records increment change number
- Status changes trigger audit log entries
- Digital signatures required for final approval
- All validation must pass before approval

================================================================================
*/

import { Row, Col, Form } from "react-bootstrap";
import PropTypes from 'prop-types';
import { useState, useImperativeHandle, forwardRef, useEffect } from "react";
import { AddNewGrooveWPQ } from "../../Services/wpq_groove-services";
import { isAllowCustomSpecialCharacters } from "../../functions/validations";
import { getPresignedURL } from "../../Services/Welder-services";
import defaultimg from "../../icons/noimage.png";
import { AddLogs } from "../../Services/wps-logs-services";
import { AddNewDetail } from "../../Services/wpq_details-service";
import { SingleInput } from "../utility/forms/inputs/InputsTransaction";
import { UnifiedSignatureSection } from "../utility/common/TransactionComponents/UnifiedSignatureSection";
import { IsUserMasterBand, getAllUserMaster } from "../../Services/user-service";
import { WPQPage3, WPQPage1, WPQ_Groove_Detail } from "./WPQObjects/WPQ_entity";
import { Action, Status } from "../utility/common/constants/CommonConstants";
import { validateForm } from "../utility/common/TransactionComponents/TransactionValidationUtils";
import { WPQHeader } from "./WPQObjects/WPQHeader";
import { PQRPage3TableColumnN } from "../utility/common/TransactionComponents/WPQ_PQR_Test_Table_Components";
import { check_TestArray } from "./WPQObjects/WPQ_Validations";
import { handleCount, emptyError, handleWPQGeneralSubmit, handleWPQGeneralSubmitTemp } from "./WPQObjects/WPQCommonUtils";
import { DotLoader } from "react-spinners";
import { showWarning } from "../utility/ui/notifications/Toaster";

const Test_and_Certificate = forwardRef((
  {
    headerObj,
    setheaderObj,
    testObj,
    detailObj,
    logObj,
    isReadonly,
    isDisabled,
    action
  },
  ref
) => {
  Test_and_Certificate.propTypes = {
    headerObj: PropTypes.array,
    setheaderObj: PropTypes.func,
    testObj: PropTypes.array,
    detailObj: PropTypes.array,
    isDisabled: PropTypes.bool,
    logObj: PropTypes.array,
    isReadonly: PropTypes.bool,
    action: PropTypes.bool,
  }
  let Band = sessionStorage.getItem("Band");
  let folderwld = "wld_signatures";
  const avatar = { defaultimg };

  const [loading, setLoading] = useState(false);
  const [IsEdited, setIsEdited] = useState(false);
  const [users, setUsers] = useState(null);
  const [previewuser, setPreviewuser] = useState(avatar.defaultimg);
  const [previewapproved, setPreviewapproved] = useState(avatar.defaultimg);
  let array = headerObj?.testarray;

  const handleChange = async (e) => {
    setIsEdited(true)
    const { name, value } = e.target;
    setheaderObj((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  const validate2 = async (detaildata) => {
    let isError = false;
    let errortype = WPQ_Groove_Detail;
    let entries = Object.entries(detaildata);

    errortype.forEach((cols) => {
      entries.forEach(([key, val]) => {
        let value = "";
        let value1 = "";
        let msg = "This field is required";

        if (cols.Type === "S") {
          value = detailObj[key][cols.Column][0].Actual_Value[0];
          value1 = detailObj[key][cols.Column][1].Range_Qualified[0];
        }
        if (cols.Type === "SA") {
          value = detailObj[key][cols.Column][0].Actual_Value[0];
          value1 = detailObj[key][cols.Column][1].Range_Qualified[0].Range_Qualified1[0];
        }

        if (
          value === undefined ||
          value === "" ||
          value === null ||
          value1 === undefined ||
          value1 === "" ||
          value1 === null
        ) {
          isError = true;
          setError((prevState) => ({
            ...prevState,
            [cols.Column]: [
              {
                Actual_Value: [{ status: true, message: msg }],
              },
              {
                Range_Qualified: [{ status: true, message: msg }],
              },
            ],
          }));
        }
      });
    });

    return isError;
  };

  const handleChangeRadio = async (e) => {
    const detaildata = detailObj.filter((item) => item.WPQ_Process !== "");
    
    if (!(await validate()) && !(await validate2(detaildata))) {
      const { name, value } = e.target;
      setheaderObj((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    } else {
      showWarning("Cannot Approve or Reject the Record, Please Fill Required Fields of all 3 Pages.");
    }
  };

  const fetchUserMaster = async () => {
    const result = await getAllUserMaster();
    const active = result.filter((item) => item.um_status === "Active")
    setUsers(active);
  };

  const fetchUserId = async () => {
    try {
      if (!headerObj || !headerObj.WPSH_ApprovedBy_Name) {
        console.error("users or headerObj.WPSH_ApprovedBy_Name is not defined");
        return;
      }
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  useEffect(() => {
    fetchUserMaster();
  }, []);

  useEffect(() => {
    fetchUserId();
  }, [users, headerObj?.WPSH_ApprovedBy_Name]);

  const handleChangetestarray = (Index, key, newValue) => {
    setIsEdited(true)
    const updatedheadertestObj = { ...headerObj }
    updatedheadertestObj.testarray[Index][key] = newValue;
    setheaderObj(updatedheadertestObj);
  };

  const [error, setError] = useState({
    WPSH_PreparedBy_Date: {},
    WPSH_PreparedBy_Name: {},
    WPSH_ApprovedBy_Date: {},
    WPSH_ApprovedBy_Name: {},
    WPSH_PreparedBy_Remark: {},
    WPSH_ApprovedBy_Remark: {},
    WPQ_WP_TypeOfTest: [],
    WPQ_WP_Acceptance_Criteria: [],
    WPQ_WP_Result: {},
    WPQ_WP_Comments: {},
    WPQ_WP_Report_Nummber: {},
    WPQ_Notes: {},
    WPQ_Certi_test_conductedby: {},
    WPQ_Certi_Mechanical_Tesstby: {},
    WPQ_Certi_RT_Film_Rvaluatedby: {},
  });

  const validate = async () => {
    const detaildata = detailObj.filter((item) => item.WPQ_Process !== "");

    const validationResult = validateForm({
      headerObj,
      detailObj: detaildata,
      requiredFields: { ...WPQPage1, ...WPQPage3 },
      detailConfig: WPQ_Groove_Detail,
      currentErrors: error,
    });

    let isError = validationResult.isError;
    let finalErrors = validationResult.errors;

    if (Band === "T" || Band === "P") {
      if (headerObj?.WPSH_ApprovedBy_Remark === "") {
        isError = true;
        finalErrors.WPSH_ApprovedBy_Remark = {
          status: true,
          message: "This field is required",
        };
      }
    }

    let norows = check_TestArray(array, "WPQ_WP_TypeOfTest", "WPQ_WP_Acceptance_Criteria", "WPQ_WP_Result",
      "WPQ_WP_Comments", "WPQ_WP_Report_Nummber", 2);

    if (norows) { isError = norows; }

    setError((prevState) => ({
      ...prevState,
      ...finalErrors,
    }));

    return isError;
  };

  const handleAdd = async (approvalstatus) => {
    let result = false;
    if (headerObj?.WPSH_Status === Status.StatusApproved) headerObj.WPS_Change_No = handleCount(headerObj.WPS_Change_No);
    if (headerObj?.WPSH_Approval_Status !== undefined)
      headerObj.WPSH_Status = headerObj?.WPSH_Approval_Status;
    else headerObj.WPSH_Status = approvalstatus;

    const detaildata = detailObj?.filter((item) => item.WPQ_Process !== "");
    detaildata?.forEach(item => {
      item.WPSH_Status = headerObj.WPSH_Status;
    });

    try {
      let res = await AddNewGrooveWPQ(headerObj)

      if (res !== undefined) {
        for (const item of detaildata) {
          await AddNewDetail(item);
        }
        result = true;
        if (action === Action.ActionEdit && approvalstatus !== Status.StatusTempSaved) {
          AddLogs(logObj);
        }
      }
      else {
        showWarning(process.env.REACT_APP_SPECIAL_CHARACTER_MSG);
      }
    }
    catch (error) {
      console.error("Error adding header:", error);
    }
    return result;
  };

  const checkIfUsernameExists = async () => {
    let res = await IsUserMasterBand();
    if (res !== undefined) return res.data.message;
    else return false;
  };

  const validateTemp = async () => {
    let isError = false;
    let temperror = emptyError(error, setError, isError);
    return { isError, temperror };
  };

  const handleSubmitTemp = () => handleWPQGeneralSubmitTemp(
    IsEdited,
    validateTemp,
    () => handleAdd(headerObj?.WPSH_Status),
    setLoading,
    setIsEdited,
    headerObj?.WPSH_Status || Status.StatusTempSaved
  );

  const handleSubmit = async () => {
    const isUsernamePresent = await checkIfUsernameExists();
    if (isUsernamePresent === false) {
      headerObj.WPSH_Status = Status.StatusTempSaved;
    }

    return await handleWPQGeneralSubmit(
      validate,
      () => handleAdd(Status.StatusPending),
      setLoading,
      setIsEdited,
      Status.StatusPending
    );
  };

  useImperativeHandle(ref, () => ({
    funcOnNext: async () => {
      let res = false;
      res = await handleSubmit();
      return res;
    },
    funcOnNextTemp: async () => {
      let res = false;
      res = await handleSubmitTemp();
      return res;
    },
  }));

  const fetchbyuserprofile = async (userprofile) => {
    try {
      if (userprofile !== "") {
        const result = await getPresignedURL(folderwld, userprofile);
        const geturl = result.url;
        setPreviewuser(geturl);
      }
      else {
        setPreviewuser(avatar.defaultimg);
      }
    } catch (error) {
      if (error.request) {
        console.log(error.request);
      } else {
        console.log(`Error: ${error.message}`);
      }
    }
  };

  const fetchapproveUserProfile = async (userprofile) => {
    try {
      if (userprofile !== "") {
        const result = await getPresignedURL(folderwld, userprofile);
        const geturl = result.url;
        setPreviewapproved(geturl);
      }
      else {
        setPreviewapproved(avatar.defaultimg);
      }
    } catch (error) {
      if (error.request) {
        console.log(error.request);
      } else {
        console.log(`Error: ${error.message}`);
      }
    }
  };

  useEffect(() => {
    if ((action === Action.ActionEdit || action === Action.ActionCopy) && headerObj?.wpq_signatureApproved !== "") {
      fetchbyuserprofile(headerObj?.wpq_signaturePrepared)
    }
    if ((action === Action.ActionEdit || action === Action.ActionCopy) && headerObj?.wpq_signatureApproved !== "") {
      fetchapproveUserProfile(headerObj?.wpq_signatureApproved)
    }
  }, []);

  return (
    <Row className="font">
      {loading && (
        <div className="loader-overlay">
          <DotLoader size={80} className="pageloader" />
        </div>
      )}
      <WPQHeader headerObj={headerObj}></WPQHeader>
      <Row style={{ padding: "20px 60px" }}>
        <Row className="mb-1">
          <Col md={12}>
            <p className="WPS-blue-text " style={{ position: "relative", top: "10px" }}>Tests </p>
            <PQRPage3TableColumnN
              columns={5}
              maxLength={[23, 15, 10, 25, 20]}
              width={[200, 120, 120, 180]}
              session="WPQ"
              array={array}
              setheaderObj={setheaderObj}
              disabled={isDisabled}
              numberofrowsdisabled={0}
              setError={setError}
              title={[
                "Type Of Test",
                "Acceptance Criteria",
                "Results",
                "Comments",
                "Report Number"
              ]}
              name={[
                "WPQ_WP_TypeOfTest",
                "WPQ_WP_Acceptance_Criteria",
                "WPQ_WP_Result",
                "WPQ_WP_Comments",
                "WPQ_WP_Report_Nummber"
              ]}
              status={[
                error.WPQ_WP_TypeOfTest.status,
                error.WPQ_WP_Acceptance_Criteria.status,
                error.WPQ_WP_Result.status,
                error.WPQ_WP_Comments.status,
                error.WPQ_WP_Report_Nummber.status
              ]}
              message={[
                error.WPQ_WP_TypeOfTest.message,
                error.WPQ_WP_Acceptance_Criteria.message,
                error.WPQ_WP_Result.message,
                error.WPQ_WP_Comments.message,
                error.WPQ_WP_Report_Nummber.message
              ]}
              handleChangetestarray={handleChangetestarray}
            />
          </Col>
        </Row>

        <Row style={{ padding: "20px 0px" }}>
          <span className="WPS-blue-text" style={{ position: "relative", top: "10px" }}>Notes</span>
          <Col md={12}>
            <div className="T-input w-100">
              <textarea
                name="WPQ_Notes"
                style={{ height: "100px" }}
                id="WPQ_Notes"
                readOnly={isReadonly}
                disabled={isDisabled}
                placeholder=""
                value={headerObj?.WPQ_Notes}
                onInput={(e) => {
                  let value = e.target.value;
                  let rawChars = value.replace(/\n/g, "");
                  if (rawChars.length > 300) {
                    rawChars = rawChars.slice(0, 300);
                  }
                  let chunks = rawChars.match(/.{1,100}/g) || [];
                  if (chunks.length > 3) {
                    chunks = chunks.slice(0, 3);
                  }
                  value = chunks.join("\n");
                  e.target.value = value;
                  handleChange(e);
                }}
              />
              <Form.Label htmlFor="WPQ_Notes">Notes</Form.Label>
            </div>
            <div className="required-text1">
              {error.WPQ_Notes.status && (
                <Form.Text className="text-danger">
                  {error.WPQ_Notes.message}
                </Form.Text>
              )}
            </div>
          </Col>
        </Row>

        <Row style={{ padding: "20px 0px" }} className="PQR-Page-row-padding">
          <span className="WPS-blue-text" style={{ position: "relative", top: "10px" }}>Certification</span>
          <Col md={6}>
            <Form.Group>
              <SingleInput
                name="WPQ_Certi_test_conductedby"
                type="text"
                maxLength={30}
                readOnly={isReadonly}
                status={error.WPQ_Certi_test_conductedby.status}
                value={headerObj?.WPQ_Certi_test_conductedby}
                onChange={handleChange}
                setError={setError}
                error={error}
                label="Test Conducted By"
                message={error.WPQ_Certi_test_conductedby.message}
                disabled={isDisabled}
                allowlistfunction={isAllowCustomSpecialCharacters}
                allowcharacters="&-"
                customMessage="Special Characters # % + $ ' < > = ; not allowed"
              ></SingleInput>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <SingleInput
                name="WPQ_Certi_Mechanical_Tesstby"
                type="text"
                maxLength={30}
                status={error.WPQ_Certi_Mechanical_Tesstby.status}
                value={headerObj?.WPQ_Certi_Mechanical_Tesstby}
                onChange={handleChange}
                setError={setError}
                error={error}
                readOnly={isReadonly}
                label="Mechanical Test By"
                message={error.WPQ_Certi_Mechanical_Tesstby.message}
                disabled={isDisabled}
                allowlistfunction={isAllowCustomSpecialCharacters}
                allowcharacters="&-"
                customMessage="Special Characters # % + $ ' < > = ; not allowed"
              ></SingleInput>
            </Form.Group>
          </Col>
          <Col md={12} className="mt-1">
            <Form.Group>
              <SingleInput
                name="WPQ_Certi_RT_Film_Rvaluatedby"
                type="text"
                maxLength={82}
                readOnly={isReadonly}
                status={error.WPQ_Certi_RT_Film_Rvaluatedby.status}
                value={headerObj?.WPQ_Certi_RT_Film_Rvaluatedby}
                onChange={handleChange}
                setError={setError}
                error={error}
                label="RT Film Evaluated By"
                message={error.WPQ_Certi_RT_Film_Rvaluatedby.message}
                disabled={isDisabled}
                allowlistfunction={isAllowCustomSpecialCharacters}
                allowcharacters="&-"
                customMessage="Special Characters # % + $ ' < > = ; not allowed"
              ></SingleInput>
            </Form.Group>
          </Col>
        </Row>
        <UnifiedSignatureSection
          sessionType="WPQ"
          sectionType="combined"
          headerObj={headerObj}
          setheaderObj={setheaderObj}
          users={users}
          error={error}
          setError={setError}
          previewImage={previewuser}
          previewApprovedImage={previewapproved}
          fetchUserProfile={fetchbyuserprofile}
          fetchApprovedUserProfile={fetchapproveUserProfile}
          isReadonly={isReadonly}
          isDisabled={isDisabled}
          action={action}
          setIsEdited={setIsEdited}
          Band={Band}
          handleChange={handleChange}
          handleChangeRadio={handleChangeRadio}
        />
      </Row>
    </Row>
  );
}
);

export default Test_and_Certificate;
